import axios from 'axios';

const PART_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_CONCURRENT_PARTS = 3;
const MAX_PART_RETRIES = 3;
const SIGN_BATCH_SIZE = 20;

function authConfig(rootState, b2c) {
  return {
    headers: {
      Authorization: b2c
        ? `Bearer ${rootState.ds24.sessionToken}`
        : rootState.auth.bearer,
    },
  };
}

async function uploadPartWithRetry(uploadURL, blob, contentType, retries = MAX_PART_RETRIES) {
  let lastError;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await axios.put(uploadURL, blob, {
        headers: { 'Content-Type': contentType || 'application/octet-stream' },
      });
      const etag = response.headers.etag || response.headers.ETag;
      if (!etag) throw new Error('Missing ETag from upload part response');
      return etag.replace(/"/g, '');
    } catch (err) {
      lastError = err;
      await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
    }
  }
  throw lastError;
}

async function capturePosterAndDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const objectUrl = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : null;
      const seekTo = duration ? Math.min(1, duration * 0.1) : 0;
      video.currentTime = seekTo;
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            resolve({
              duration: Number.isFinite(video.duration) ? video.duration : null,
              posterBlob: blob,
            });
          },
          'image/jpeg',
          0.85
        );
      } catch (_err) {
        URL.revokeObjectURL(objectUrl);
        resolve({ duration: null, posterBlob: null });
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ duration: null, posterBlob: null });
    };

    video.src = objectUrl;
  });
}

async function runPool(items, worker, concurrency) {
  const results = new Array(items.length);
  let index = 0;

  async function runWorker() {
    while (index < items.length) {
      const current = index++;
      results[current] = await worker(items[current], current);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => runWorker()
  );
  await Promise.all(workers);
  return results;
}

const state = () => ({
  uploads: {},
});

const mutations = {
  SET_UPLOAD_PROGRESS(state, { videoId, progress }) {
    state.uploads = {
      ...state.uploads,
      [videoId]: { ...(state.uploads[videoId] || {}), progress },
    };
  },
  CLEAR_UPLOAD(state, videoId) {
    const next = { ...state.uploads };
    delete next[videoId];
    state.uploads = next;
  },
};

const actions = {
  async uploadVideo({ commit, rootState }, payload) {
    const {
      file,
      journeyId,
      folderName,
      onProgress,
      b2c = false,
    } = payload;

    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = authConfig(rootState, b2c);

    const createRes = await axios.post(
      '/v2/video/upload/create',
      {
        journeyId,
        folder_name: folderName,
        contentType: file.type,
        originalFilename: file.name,
        sizeBytes: file.size,
      },
      config
    );

    const { videoId, uploadId, partSize } = createRes.data;
    const chunkSize = partSize || PART_SIZE;
    const totalParts = Math.ceil(file.size / chunkSize);
    const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

    const signedByPart = new Map();
    const signingLocks = new Map();

    async function ensurePartSigned(partNumber) {
      if (signedByPart.has(partNumber)) return;

      const batchStart =
        Math.floor((partNumber - 1) / SIGN_BATCH_SIZE) * SIGN_BATCH_SIZE + 1;
      const batchEnd = Math.min(batchStart + SIGN_BATCH_SIZE - 1, totalParts);
      const lockKey = `${batchStart}-${batchEnd}`;

      if (!signingLocks.has(lockKey)) {
        const batch = [];
        for (let p = batchStart; p <= batchEnd; p++) {
          if (!signedByPart.has(p)) batch.push(p);
        }
        if (batch.length > 0) {
          signingLocks.set(
            lockKey,
            axios
              .post('/v2/video/upload/sign-parts', { videoId, partNumbers: batch }, config)
              .then((signRes) => {
                for (const p of signRes.data.parts || []) {
                  signedByPart.set(p.partNumber, p.uploadURL);
                }
              })
          );
        } else {
          signingLocks.set(lockKey, Promise.resolve());
        }
      }

      await signingLocks.get(lockKey);
    }

    let uploadedBytes = 0;
    const completedParts = await runPool(
      partNumbers,
      async (partNumber) => {
        await ensurePartSigned(partNumber);
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);
        const uploadURL = signedByPart.get(partNumber);
        const etag = await uploadPartWithRetry(uploadURL, blob, file.type);
        uploadedBytes += blob.size;
        const progress = Math.round((uploadedBytes / file.size) * 100);
        commit('SET_UPLOAD_PROGRESS', { videoId, progress });
        if (onProgress) onProgress(progress);
        return { PartNumber: partNumber, ETag: etag };
      },
      MAX_CONCURRENT_PARTS
    );

    const { duration, posterBlob } = await capturePosterAndDuration(file);
    let posterUploaded = false;
    let posterUrl = null;

    if (posterBlob) {
      posterUrl = URL.createObjectURL(posterBlob);
      const posterRes = await axios.post(
        `/v2/video/${encodeURIComponent(journeyId)}/${encodeURIComponent(videoId)}/poster`,
        {},
        config
      );
      await axios.put(posterRes.data.uploadURL, posterBlob, {
        headers: { 'Content-Type': 'image/jpeg' },
      });
      posterUploaded = true;
    }

    const completeRes = await axios.post(
      '/v2/video/upload/complete',
      {
        videoId,
        parts: completedParts,
        durationSeconds: duration,
        posterUploaded,
      },
      config
    );

    commit('CLEAR_UPLOAD', videoId);
    return {
      videoId,
      status: completeRes.data.status,
      duration,
      originalFilename: file.name,
      posterUrl,
    };
  },

  async listJourneyVideos({ rootState }, { journeyId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = authConfig(rootState, false);
    const response = await axios.get(
      `/v2/video/${encodeURIComponent(journeyId)}/list`,
      config
    );
    return response.data.videos || [];
  },

  async deleteVideo({ rootState }, { journeyId, videoId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    try {
      await axios.delete(
        `/v2/video/${encodeURIComponent(journeyId)}/${encodeURIComponent(videoId)}`,
        authConfig(rootState, false)
      );
      return { ok: true };
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        'Could not delete the video. Please try again.';
      throw new Error(message);
    }
  },

  async getManifest({ rootState }, { journeyId, videoId, b2c = false }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = authConfig(rootState, b2c);
    const base = b2c ? '/v2/ds24/video' : '/v2/video';
    const response = await axios.get(
      `${base}/${encodeURIComponent(journeyId)}/${encodeURIComponent(videoId)}/manifest`,
      config
    );
    return response.data;
  },

  async reprocessJourneyHls({ rootState }, { journeyId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const response = await axios.post(
      `/v2/video/${encodeURIComponent(journeyId)}/reprocess-hls`,
      {},
      authConfig(rootState, false)
    );
    return response.data;
  },

  async uploadCaption({ rootState }, { journeyId, videoId, lang, label, file }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    const config = authConfig(rootState, false);
    const response = await axios.post(
      `/v2/video/${encodeURIComponent(journeyId)}/${encodeURIComponent(videoId)}/captions`,
      { lang, label },
      config
    );
    await axios.put(response.data.uploadURL, file, {
      headers: { 'Content-Type': 'text/vtt' },
    });
    return response.data;
  },

  async abortUpload({ rootState }, { videoId }) {
    axios.defaults.baseURL = rootState.tenant.tenantinfo.baseAPI;
    await axios.post(
      '/v2/video/upload/abort',
      { videoId },
      authConfig(rootState, false)
    );
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
